import React, { useEffect, useState } from 'react'
import { clientService } from '../services/api'
import TableStateWrapper from '../components/TableStateWrapper'
import TablePagination from '../components/TablePagination'
import useResponsivePageSize from '../hooks/useResponsivePageSize'
import useModalFeedback from '../hooks/useModalFeedback'
import useRolePermissions from '../hooks/useRolePermissions'
import TruncatedText from '../components/TruncatedText'
import CollapsibleFilters from '../components/CollapsibleFilters'
import MultiSelectFilterDropdown from '../components/MultiSelectFilterDropdown'

export default function Clients() {
  const { showMessage, showConfirm, modalElement } = useModalFeedback()
  const { isViewer, canMutate } = useRolePermissions()
  const [clients, setClients] = useState([])
  const [loading, setLoading] = useState(false)
  const [tableError, setTableError] = useState(null)
  const [showModal, setShowModal] = useState(false)
  const [editingId, setEditingId] = useState(null)
  const [clientPage, setClientPage] = useState(1)
  const [filters, setFilters] = useState({
    client_no: [],
    client_name: [],
    registration_no: [],
    address_line_1: '',
    address_line_2: '',
    address_line_3: '',
    address_line_4: ''
  })
  const [formData, setFormData] = useState({
    client_name: '',
    registration_no: '',
    address_line_1: '',
    address_line_2: '',
    address_line_3: '',
    address_line_4: ''
  })

  const { containerRef: clientTableRef, rowsPerPage: clientRowsPerPage } = useResponsivePageSize({
    reservedHeight: 560,
    footerHeight: 24,
    rowHeight: 52,
    minRows: 5,
    maxRows: 14
  })

  useEffect(() => {
    fetchClients()
  }, [])

  useEffect(() => {
    const handleFocus = () => fetchClients()
    window.addEventListener('focus', handleFocus)
    return () => window.removeEventListener('focus', handleFocus)
  }, [])

  useEffect(() => {
    setClientPage(1)
  }, [filters, clientRowsPerPage])

  const fetchClients = async () => {
    try {
      setLoading(true)
      setTableError(null)
      const response = await clientService.getAll()
      setClients(response.data.data || [])
    } catch (error) {
      console.error('Failed to fetch clients:', error)
      setTableError(error.response?.data?.message || 'Failed to fetch clients')
    } finally {
      setLoading(false)
    }
  }

  const handleFilterChange = (e) => {
    const { name, value } = e.target
    setFilters((prev) => ({ ...prev, [name]: value }))
  }

  const getUniqueFieldValues = (field) => {
    return [...new Set(clients.map((client) => String(client[field] || '').trim()).filter(Boolean))].sort((a, b) => a.localeCompare(b))
  }

  const clearAllFilters = () => {
    setFilters({
      client_no: [],
      client_name: [],
      registration_no: [],
      address_line_1: '',
      address_line_2: '',
      address_line_3: '',
      address_line_4: ''
    })
  }

  const getFilteredClients = () => {
    return clients.filter((client) => {
      const matchClientNo = filters.client_no.length === 0 || filters.client_no.includes(String(client.client_no || ''))
      const matchClientName = filters.client_name.length === 0 || filters.client_name.includes(String(client.client_name || ''))
      const matchRegNo = filters.registration_no.length === 0 || filters.registration_no.includes(String(client.registration_no || ''))
      const matchAddress1 = !filters.address_line_1 || String(client.address_line_1 || '') === filters.address_line_1
      const matchAddress2 = !filters.address_line_2 || String(client.address_line_2 || '') === filters.address_line_2
      const matchAddress3 = !filters.address_line_3 || String(client.address_line_3 || '') === filters.address_line_3
      const matchAddress4 = !filters.address_line_4 || String(client.address_line_4 || '') === filters.address_line_4

      return matchClientNo && matchClientName && matchRegNo && matchAddress1 && matchAddress2 && matchAddress3 && matchAddress4
    })
  }

  const filteredClients = getFilteredClients()
  const totalClientPages = Math.max(1, Math.ceil(filteredClients.length / clientRowsPerPage))
  const paginatedClients = filteredClients.slice((clientPage - 1) * clientRowsPerPage, clientPage * clientRowsPerPage)

  useEffect(() => {
    setClientPage((prev) => Math.min(prev, totalClientPages))
  }, [totalClientPages])

  const handleInputChange = (e) => {
    const { name, value } = e.target
    setFormData((prev) => ({ ...prev, [name]: value }))
  }

  const openCreateModal = () => {
    setEditingId(null)
    setFormData({
      client_name: '',
      registration_no: '',
      address_line_1: '',
      address_line_2: '',
      address_line_3: '',
      address_line_4: ''
    })
    setShowModal(true)
  }

  const openEditModal = (client) => {
    setEditingId(client.client_id)
    setFormData({
      client_name: client.client_name || '',
      registration_no: client.registration_no || '',
      address_line_1: client.address_line_1 || '',
      address_line_2: client.address_line_2 || '',
      address_line_3: client.address_line_3 || '',
      address_line_4: client.address_line_4 || ''
    })
    setShowModal(true)
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    try {
      if (!formData.client_name.trim()) {
        showMessage('Client Name is required', 'Validation')
        return
      }

      const isEditing = editingId !== null && editingId !== undefined

      setLoading(true)

      if (isEditing) {
        await clientService.update(editingId, formData)
      } else {
        await clientService.create(formData)
      }

      setShowModal(false)
      setEditingId(null)
      setFormData({
        client_name: '',
        registration_no: '',
        address_line_1: '',
        address_line_2: '',
        address_line_3: '',
        address_line_4: ''
      })

      showMessage(isEditing ? 'Client updated successfully!' : 'Client created successfully!', 'Success')
      await fetchClients()
    } catch (error) {
      console.error('Failed to save client:', error)
      showMessage(error.response?.data?.message || 'Failed to save client', 'Error')
    } finally {
      setLoading(false)
    }
  }

  const handleDeleteClient = async (clientId) => {
    const confirmed = await showConfirm('Are you sure you want to delete this client? This action cannot be undone.')
    if (!confirmed) {
      return
    }

    try {
      setLoading(true)
      await clientService.delete(clientId)
      showMessage('Client deleted successfully!', 'Success')
      await fetchClients()
    } catch (error) {
      console.error('Failed to delete client:', error)
      showMessage(error.response?.data?.message || 'Failed to delete client', 'Error')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div>
      <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <h1 className="text-2xl font-bold text-gray-800 sm:text-3xl">Clients</h1>
        {canMutate && (
          <button
            onClick={openCreateModal}
            className="w-full rounded-lg bg-blue-600 px-4 py-2 font-medium text-white transition hover:bg-blue-700 sm:w-auto"
          >
            + Add Client
          </button>
        )}
      </div>

      <CollapsibleFilters>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <MultiSelectFilterDropdown
            label="Filter by Client No."
            options={getUniqueFieldValues('client_no')}
            selectedValues={filters.client_no}
            onChange={(values) => setFilters((prev) => ({ ...prev, client_no: values }))}
            placeholder="All Client No."
            emptyText="No client numbers"
          />

          <MultiSelectFilterDropdown
            label="Filter by Client Name"
            options={getUniqueFieldValues('client_name')}
            selectedValues={filters.client_name}
            onChange={(values) => setFilters((prev) => ({ ...prev, client_name: values }))}
            placeholder="All Client Names"
            emptyText="No client names"
          />

          <MultiSelectFilterDropdown
            label="Filter by Registration No."
            options={getUniqueFieldValues('registration_no')}
            selectedValues={filters.registration_no}
            onChange={(values) => setFilters((prev) => ({ ...prev, registration_no: values }))}
            placeholder="All Registration No."
            emptyText="No registration numbers"
          />

          <div className="flex items-end">
            <button
              onClick={clearAllFilters}
              className="btn-reset-standard"
            >
              🔄 Reset Filters
            </button>
          </div>
        </div>
      </CollapsibleFilters>

      <TableStateWrapper
        loading={loading}
        error={tableError}
        onRetry={fetchClients}
        loadingText="Loading client records..."
        containerRef={clientTableRef}
      >
        <table className="w-full">
          <thead className="bg-gray-100 border-b">
            <tr>
              <th className="px-3 py-2 text-left text-sm font-semibold text-gray-700 whitespace-nowrap">Client No.</th>
              <th className="px-3 py-2 text-left text-sm font-semibold text-gray-700 whitespace-nowrap">Client Name</th>
              <th className="px-3 py-2 text-left text-sm font-semibold text-gray-700 whitespace-nowrap">Registration No.</th>
              <th className="px-3 py-2 text-left text-sm font-semibold text-gray-700 whitespace-nowrap">Address Line 1</th>
              <th className="px-3 py-2 text-left text-sm font-semibold text-gray-700 whitespace-nowrap">Address Line 2</th>
              <th className="px-3 py-2 text-left text-sm font-semibold text-gray-700 whitespace-nowrap">Address Line 3</th>
              <th className="px-3 py-2 text-left text-sm font-semibold text-gray-700 whitespace-nowrap">Address Line 4</th>
              {!isViewer && <th className="px-3 py-2 text-center text-sm font-semibold text-gray-700 whitespace-nowrap">Action</th>}
            </tr>
          </thead>
          <tbody>
            {filteredClients.length === 0 ? (
              <tr>
                <td colSpan={isViewer ? '7' : '8'} className="px-3 py-2 text-center text-gray-500">
                  No clients yet. Create your first client!
                </td>
              </tr>
            ) : (
              paginatedClients.map((client) => (
                <tr
                  key={client.client_id}
                  onClick={() => openEditModal(client)}
                  className="border-b hover:bg-blue-50 cursor-pointer transition"
                >
                  <td className="px-3 py-2 text-sm font-semibold text-blue-600 whitespace-nowrap">{client.client_no}</td>
                  <td className="px-3 py-2 text-sm font-medium text-gray-800 whitespace-nowrap"><TruncatedText text={client.client_name} /></td>
                  <td className="px-3 py-2 text-sm text-gray-700 whitespace-nowrap">{client.registration_no || '-'}</td>
                  <td className="px-3 py-2 text-sm text-gray-700 whitespace-nowrap"><TruncatedText text={client.address_line_1} /></td>
                  <td className="px-3 py-2 text-sm text-gray-700 whitespace-nowrap"><TruncatedText text={client.address_line_2} /></td>
                  <td className="px-3 py-2 text-sm text-gray-700 whitespace-nowrap"><TruncatedText text={client.address_line_3} /></td>
                  <td className="px-3 py-2 text-sm text-gray-700 whitespace-nowrap"><TruncatedText text={client.address_line_4} /></td>
                  {!isViewer && (
                    <td className="px-3 py-2 text-center whitespace-nowrap">
                      <button
                        onClick={(event) => {
                          event.stopPropagation()
                          openEditModal(client)
                        }}
                        className="text-blue-600 hover:text-blue-800 text-sm font-medium mr-3"
                        title="Edit"
                      >
                        ✏️
                      </button>
                      <button
                        onClick={() => handleDeleteClient(client.client_id)}
                        className="text-red-600 hover:text-red-800 text-sm font-medium"
                        title="Delete"
                      >
                        🗑️
                      </button>
                    </td>
                  )}
                </tr>
              ))
            )}
          </tbody>
        </table>

        <TablePagination
          currentPage={clientPage}
          totalPages={totalClientPages}
          totalItems={filteredClients.length}
          rowsPerPage={clientRowsPerPage}
          onPrevious={() => setClientPage((prev) => Math.max(prev - 1, 1))}
          onNext={() => setClientPage((prev) => Math.min(prev + 1, totalClientPages))}
        />
      </TableStateWrapper>

      {showModal && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
          onClick={() => setShowModal(false)}
        >
          <div
            className="modal-panel-standard bg-white max-w-3xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="mb-6 flex items-start justify-between gap-3">
              <h2 className="text-2xl font-bold">
                {editingId ? 'Edit Client' : 'Add New Client'}
              </h2>
              <button
                type="button"
                onClick={() => setShowModal(false)}
                className="modal-close-button"
                aria-label="Close modal"
              >
                ×
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Client Name</label>
                <input
                  type="text"
                  name="client_name"
                  value={formData.client_name}
                  onChange={handleInputChange}
                  placeholder="Client company name"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Registration No.</label>
                <input
                  type="text"
                  name="registration_no"
                  value={formData.registration_no}
                  onChange={handleInputChange}
                  placeholder="Registration number"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Address Line 1</label>
                <input
                  type="text"
                  name="address_line_1"
                  value={formData.address_line_1}
                  onChange={handleInputChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Address Line 2</label>
                <input
                  type="text"
                  name="address_line_2"
                  value={formData.address_line_2}
                  onChange={handleInputChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Address Line 3</label>
                <input
                  type="text"
                  name="address_line_3"
                  value={formData.address_line_3}
                  onChange={handleInputChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Address Line 4</label>
                <input
                  type="text"
                  name="address_line_4"
                  value={formData.address_line_4}
                  onChange={handleInputChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                />
              </div>

              <div className="flex gap-3 pt-3">
                <button
                  type="submit"
                  disabled={!canMutate || loading}
                  className="flex-1 bg-blue-600 hover:bg-blue-700 text-white py-2 rounded-lg font-medium"
                >
                  {editingId ? 'Update Client' : 'Create Client'}
                </button>
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="flex-1 bg-gray-500 hover:bg-gray-600 text-white py-2 rounded-lg font-medium"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      {modalElement}
    </div>
  )
}
